/**
 * Orders Page Component
 * 
 * This component displays the orders management interface
 * with filtering, search, and order management capabilities.
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { useTranslations } from '../context/TranslationsContext';
import { ordersAPI } from '../api';
import OrdersTable from '../components/orders/OrdersTable';
import OrderDetailsModal from '../components/orders/OrderDetailsModal';
import StatusUpdateModal from '../components/orders/StatusUpdateModal';
import SearchBar from '../components/common/SearchBar';
import Pagination from '../components/common/Pagination';

const OrdersPage = () => {
  const { state, actions } = useApp();
  const { translations } = useTranslations();
  const t = translations[state.currentLanguage];

  const [filters, setFilters] = useState({
    status: '',
    limit: 10,
    skip: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [statusUpdateOrder, setStatusUpdateOrder] = useState(null);
  const [totalOrders, setTotalOrders] = useState(0);

  useEffect(() => {
    const fetchOrders = async () => {
      if (!state.token) return;
      
      actions.setLoading(true);
      try {
        const response = await ordersAPI.getOrders(state.token, filters);
        if (Array.isArray(response)) {
          actions.setOrders(response);
          setTotalOrders(response.length);
        } else if (response && response.orders) {
          actions.setOrders(response.orders);
          setTotalOrders(response.total || response.orders.length);
        }
      } catch (error) {
        console.error('Error fetching orders:', error);
      } finally {
        actions.setLoading(false);
      }
    };

    fetchOrders();
  }, [state.token, filters]);

  // Filter orders based on search term
  const filteredOrders = useMemo(() => {
    if (!searchTerm) return state.orders;
    
    return state.orders.filter(order => 
      order.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer_phone?.includes(searchTerm) ||
      order.id?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [state.orders, searchTerm]);

  // Paginate filtered orders
  const paginatedOrders = useMemo(() => {
    const startIndex = (currentPage - 1) * filters.limit;
    const endIndex = startIndex + filters.limit;
    return filteredOrders.slice(startIndex, endIndex);
  }, [filteredOrders, currentPage, filters.limit]);

  const totalPages = Math.ceil(filteredOrders.length / filters.limit);

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleSearchChange = (term) => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset to first page when search changes
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleOrderApproval = async (orderId, status, notes = '') => {
    try {
      await ordersAPI.approveOrder(state.token, orderId, {
        order_id: orderId,
        status,
        admin_notes: notes
      });
      
      // Refresh orders
      const orders = await ordersAPI.getOrders(state.token, filters);
      actions.setOrders(orders);
      
      // Close modal
      actions.setSelectedOrder(null);
    } catch (error) {
      console.error('Error approving order:', error);
      alert('Error updating order status');
    }
  };

  const handleStatusUpdate = (order) => {
    setStatusUpdateOrder(order);
  };

  const handleStatusUpdated = async () => {
    // Refresh orders after status update
    try {
      const orders = await ordersAPI.getOrders(state.token, filters);
      actions.setOrders(orders);
    } catch (error) {
      console.error('Error refreshing orders:', error);
    }
  };

  const handlePriceUpdate = async (orderId, price) => {
    try {
      await ordersAPI.updateOrderPrice(state.token, orderId, price);
      
      // Update the order in the current list
      const updatedOrders = state.orders.map(order => 
        order.id === orderId ? { ...order, total_price: price } : order
      );
      actions.setOrders(updatedOrders);
      
      // Update selected order if it's the same one
      if (state.selectedOrder && state.selectedOrder.id === orderId) {
        actions.setSelectedOrder({ ...state.selectedOrder, total_price: price });
      }
    } catch (error) {
      console.error('Error updating order price:', error);
      alert('Error updating order price');
    }
  };

  if (state.loading) {
    return <div className="loading">جاري التحميل...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="page-header">
        <h2 className="section-title">{t.orders}</h2>
        <div className="page-actions flex gap-4">
          <SearchBar
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            placeholder="البحث في الطلبات..."
            className="w-64"
          />
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange({ status: e.target.value })}
            className="form-input"
          >
            <option value="">جميع الحالات</option>
            <option value="pending">{t.pending}</option>
            <option value="approved">{t.approved}</option>
            <option value="rejected">{t.rejected}</option>
            <option value="active">{t.active}</option>
            <option value="in_warehouse">{t.in_warehouse}</option>
            <option value="delivering_to_customer">{t.delivering_to_customer}</option>
            <option value="delivering_to_warehouse">{t.delivering_to_warehouse}</option>
            <option value="completed">{t.completed}</option>
            <option value="needs_attention">{t.needs_attention}</option>
            <option value="retrieval_requested">{t.retrieval_requested}</option>
            <option value="retrieval_approved">{t.retrieval_approved}</option>
            <option value="retrieval_delivering">{t.retrieval_delivering}</option>
            <option value="retrieval_completed">{t.retrieval_completed}</option>
          </select>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg">
        <OrdersTable 
          orders={paginatedOrders}
          onViewDetails={(order) => actions.setSelectedOrder(order)}
          onApprove={(orderId, status, notes) => handleOrderApproval(orderId, status, notes)}
          onUpdateStatus={handleStatusUpdate}
        />
        
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          totalItems={filteredOrders.length}
          itemsPerPage={filters.limit}
        />
      </div>

      {state.selectedOrder && (
        <OrderDetailsModal
          order={state.selectedOrder}
          onClose={() => actions.setSelectedOrder(null)}
          onApprove={handleOrderApproval}
          onUpdatePrice={handlePriceUpdate}
        />
      )}

      {statusUpdateOrder && (
        <StatusUpdateModal
          order={statusUpdateOrder}
          onClose={() => setStatusUpdateOrder(null)}
          onStatusUpdated={handleStatusUpdated}
        />
      )}
    </div>
  );
};

export default OrdersPage;
